/**
 * Einziger typisierter Client der Autopilot-API (/api/v1). Alle Pfade, Methoden und
 * Nutzlasten stehen hier, damit Abweichungen des Backends an einer Stelle angepasst
 * werden können. Pfade nach docs/autopilot/03 „Eigene API“ und 08 §7; als Annahme
 * markierte Pfade stehen zusätzlich in docs/contract-changes.md („Anfrage Web“).
 */
import type {
  AgentProfileView,
  ApiCatalogs,
  AuditEventView,
  BatchDetail,
  BatchFilter,
  BatchSummary,
  BatchTicketDetail,
  BatchTicketRow,
  CompanyAutomationView,
  EligibilityResponse,
  ExclusionListView,
  ExecutionRunSummary,
  HoldView,
  Me,
  ModelConfigView,
  OperationsView,
  Problem,
  ReviewProfileView,
  ScheduleRunView,
  UpdateCompanyAutomationRequest,
  UserView as UserViewSchema,
} from '@autopilot/contracts';
import type { z } from 'zod';
import { env } from '../env';
import { ApiError } from './errors';

export type Page<T> = { items: T[]; nextCursor: string | null };

export type ConnectionTestResult = { ok: boolean; resolvedModelId: string | null; message: string };
export type ClaimView = { batchTicketId: string; until: string; fencingToken: number };
export type ExclusionProbeResponse = { hits: { term: string; index: number }[] };
export type UserView = z.infer<typeof UserViewSchema>;
/** Seit „Antwort Backend“ im Vertrag: generatorModels + reviewProfile für Operatoren. */
export type CatalogsExtended = ApiCatalogs;

export type TicketListParams = {
  cursor?: string;
  limit?: number;
  filter?: 'all' | 'eligible' | 'blocked' | 'failed' | 'excluded' | 'needs_review';
  sort?: 'nummer' | 'correctness' | 'errorRisk' | 'gesellschaft' | 'status';
  dir?: 'asc' | 'desc';
  minCorrectness?: number;
  maxErrorRisk?: number;
  /** Filter nach primärer Ergebniskategorie (Links aus der Zusammenfassung). */
  resultCategory?: string;
};

export type AuditQuery = {
  cursor?: string;
  limit?: number;
  action?: string;
  actorType?: 'USER' | 'SYSTEM' | 'SERVICE';
  batchId?: string;
  from?: string;
  to?: string;
};

export type BatchListQuery = { cursor?: string; limit?: number; trigger?: 'MANUAL' | 'SCHEDULED'; phase?: string };

type Query = Record<string, string | number | boolean | undefined | null>;

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Ohne Bearer-Token (Dev-Login). */
  anonymous?: boolean;
};

let tokenProvider: (() => Promise<string | null>) | null = null;
let unauthenticatedHandler: (() => void) | null = null;

export function setTokenProvider(fn: (() => Promise<string | null>) | null) {
  tokenProvider = fn;
}
export function setUnauthenticatedHandler(fn: (() => void) | null) {
  unauthenticatedHandler = fn;
}

function buildUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return `${env.apiBase}${path}${s ? `?${s}` : ''}`;
}

async function transport(url: string, init: RequestInit): Promise<Response> {
  // Direkt auf import.meta.env prüfen, damit Vite den Mock im Normalbuild vollständig entfernt.
  if (import.meta.env.VITE_API_MOCK === '1' || import.meta.env.VITE_API_MOCK === 'true') {
    const { mockFetch } = await import('./mock/server');
    return mockFetch(url, init);
  }
  return fetch(url, init);
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json, application/problem+json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  if (!opts.anonymous && tokenProvider) {
    const token = await tokenProvider();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let res: Response;
  try {
    res = await transport(buildUrl(path, opts.query), {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err;
    throw new ApiError(0, null, 'NETWORK');
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = new ApiError(res.status, (data as Partial<Problem>) ?? null);
    if (res.status === 401 && unauthenticatedHandler) unauthenticatedHandler();
    throw err;
  }
  return data as T;
}

const enc = encodeURIComponent;

export const api = {
  // Auth / Session
  devToken: (email: string) =>
    request<{ accessToken?: string; token?: string; expiresIn?: number }>('/auth/dev/token', {
      method: 'POST',
      body: { email },
      anonymous: true,
    }),
  me: () => request<Me>('/me'),
  catalogs: () => request<CatalogsExtended>('/catalogs'),

  // Batches
  createBatch: (body: { name?: string; filter: BatchFilter }, idempotencyKey: string) =>
    request<BatchDetail>('/batches', { method: 'POST', body, idempotencyKey }),
  listBatches: (q: BatchListQuery = {}) => request<Page<BatchSummary & { filter?: BatchFilter }>>('/batches', { query: q }),
  getBatch: (id: string) => request<BatchDetail>(`/batches/${enc(id)}`),
  preheat: (id: string, body: { expectedRowVersion: number; generatorModelConfigId: string }, idempotencyKey: string) =>
    request<BatchDetail>(`/batches/${enc(id)}/preheat`, { method: 'POST', body, idempotencyKey }),
  /** SELECTING/PREHEATING → PAUSED (fortsetzbar); in EXECUTING werden die Läufe gestoppt. */
  stopBatch: (id: string, body: { expectedRowVersion: number }) =>
    request<BatchDetail>(`/batches/${enc(id)}/stop`, { method: 'POST', body }),
  resumeBatch: (id: string, body: { expectedRowVersion: number }) =>
    request<BatchDetail>(`/batches/${enc(id)}/resume`, { method: 'POST', body }),
  cancelBatch: (id: string, body: { expectedRowVersion: number }) =>
    request<BatchDetail>(`/batches/${enc(id)}/cancel`, { method: 'POST', body }),
  listTickets: (id: string, q: TicketListParams) =>
    request<Page<BatchTicketRow>>(`/batches/${enc(id)}/tickets`, { query: q }),
  eligibility: (id: string, minCorrectness: number, maxErrorRisk: number, signal?: AbortSignal) =>
    request<EligibilityResponse>(`/batches/${enc(id)}/eligibility`, { query: { minCorrectness, maxErrorRisk }, signal }),
  createExecutionRun: (
    id: string,
    body: { expectedRowVersion: number; minCorrectness: number; maxErrorRisk: number; expectedEligibleCount: number },
    idempotencyKey: string,
  ) => request<ExecutionRunSummary>(`/batches/${enc(id)}/execution-runs`, { method: 'POST', body, idempotencyKey }),
  followup: (id: string, batchTicketIds: string[], idempotencyKey: string) =>
    request<BatchDetail>(`/batches/${enc(id)}/followup`, { method: 'POST', body: { batchTicketIds }, idempotencyKey }),

  // Tickets
  getTicket: (id: string) => request<BatchTicketDetail>(`/batch-tickets/${enc(id)}`),
  updateProposal: (id: string, body: { expectedVersion: number; text: string }) =>
    request<BatchTicketDetail>(`/batch-tickets/${enc(id)}/proposal`, { method: 'PATCH', body }),
  evaluate: (id: string, body: { expectedVersion: number }, idempotencyKey: string) =>
    request<BatchTicketDetail>(`/batch-tickets/${enc(id)}/evaluate`, { method: 'POST', body, idempotencyKey }),
  setExclusion: (id: string, body: { excluded: boolean; expectedRowVersion: number }) =>
    request<BatchTicketRow>(`/batch-tickets/${enc(id)}/exclusion`, { method: 'PATCH', body }),
  claim: (id: string) => request<ClaimView>(`/batch-tickets/${enc(id)}/claim`, { method: 'POST', body: {} }),
  releaseClaim: (id: string) => request<void>(`/batch-tickets/${enc(id)}/claim`, { method: 'DELETE' }),

  // Ausführung
  getExecutionRun: (id: string) => request<ExecutionRunSummary>(`/execution-runs/${enc(id)}`),
  stopExecutionRun: (id: string) => request<ExecutionRunSummary>(`/execution-runs/${enc(id)}/stop`, { method: 'POST', body: {} }),

  // Holds (Nidus-Ticket-ID)
  createHold: (nidusTicketId: string, reason: string) =>
    request<HoldView>(`/tickets/${enc(nidusTicketId)}/automation-holds`, { method: 'POST', body: { reason } }),
  listHolds: (nidusTicketId: string) => request<HoldView[]>(`/tickets/${enc(nidusTicketId)}/automation-holds`),
  releaseHold: (nidusTicketId: string, holdId: string, note?: string) =>
    request<void>(`/tickets/${enc(nidusTicketId)}/automation-holds/${enc(holdId)}`, { method: 'DELETE', query: { note } }),

  // Audit
  auditEvents: (q: AuditQuery) => request<Page<AuditEventView>>('/audit-events', { query: q }),

  // Admin: Gesellschaften (08 §7)
  listCompanies: (q: { cursor?: string; limit?: number } = {}) =>
    request<Page<CompanyAutomationView>>('/admin/companies', { query: q }),
  getCompanyAutomation: (id: string) => request<CompanyAutomationView>(`/admin/companies/${enc(id)}/automation`),
  putCompanyAutomation: (id: string, body: UpdateCompanyAutomationRequest, idempotencyKey: string) =>
    request<CompanyAutomationView>(`/admin/companies/${enc(id)}/automation`, { method: 'PUT', body, idempotencyKey }),
  companyRuns: (id: string, q: { cursor?: string; limit?: number } = {}) =>
    request<Page<ScheduleRunView>>(`/admin/companies/${enc(id)}/automation/runs`, { query: q }),

  // Admin: Bearbeiter (Annahme: POST/PATCH /admin/agents[/{id}])
  listAgents: () => request<Page<AgentProfileView> | AgentProfileView[]>('/admin/agents').then(asItems),
  createAgent: (body: { displayName: string; signature: string | null; gesellschaftIds: string[] }) =>
    request<AgentProfileView>('/admin/agents', { method: 'POST', body }),
  updateAgent: (
    id: string,
    body: { expectedRowVersion: number; displayName?: string; signature?: string | null; gesellschaftIds?: string[]; active?: boolean },
  ) => request<AgentProfileView>(`/admin/agents/${enc(id)}`, { method: 'PATCH', body }),

  // Admin: Modelle (Annahme: POST /admin/models, PUT /admin/models/{id})
  listModels: () => request<Page<ModelConfigView> | ModelConfigView[]>('/admin/models').then(asItems),
  createModel: (body: ModelUpsert) => request<ModelConfigView>('/admin/models', { method: 'POST', body }),
  updateModel: (id: string, body: ModelUpsert) => request<ModelConfigView>(`/admin/models/${enc(id)}`, { method: 'PUT', body }),
  setModelSecret: (id: string, secret: string) =>
    request<ModelConfigView>(`/admin/models/${enc(id)}/secret`, { method: 'PUT', body: { secret } }),
  testModel: (id: string) => request<ConnectionTestResult>(`/admin/models/${enc(id)}/test`, { method: 'POST', body: {} }),

  // Admin: Prüfprofile (Annahme: POST /admin/review-profiles, PUT /admin/review-profiles/{id})
  listReviewProfiles: () => request<Page<ReviewProfileView> | ReviewProfileView[]>('/admin/review-profiles').then(asItems),
  createReviewProfile: (body: ReviewProfileUpsert) => request<ReviewProfileView>('/admin/review-profiles', { method: 'POST', body }),
  updateReviewProfile: (id: string, body: ReviewProfileUpsert) =>
    request<ReviewProfileView>(`/admin/review-profiles/${enc(id)}`, { method: 'PUT', body }),

  // Admin: Negativliste (Annahme: DELETE /admin/exclusion-terms/{id}?expectedVersion=)
  exclusionList: () => request<ExclusionListView>('/admin/exclusion-terms'),
  addExclusionTerm: (input: string, expectedVersion: number) =>
    request<ExclusionListView>('/admin/exclusion-terms', { method: 'POST', body: { input, expectedVersion } }),
  removeExclusionTerm: (id: string, expectedVersion: number) =>
    request<ExclusionListView>(`/admin/exclusion-terms/${enc(id)}`, { method: 'DELETE', query: { expectedVersion } }),
  probeExclusion: (text: string) =>
    request<ExclusionProbeResponse>('/admin/exclusion-terms/probe', { method: 'POST', body: { text } }),

  // Admin: Betrieb
  operations: () => request<OperationsView>('/admin/operations'),
  setExecutionGate: (enabled: boolean, expectedVersion: number) =>
    request<OperationsView>('/admin/operations/execution-gate', { method: 'PUT', body: { enabled, expectedVersion } }),

  // Admin: Nutzer
  listUsers: () => request<Page<UserView> | UserView[]>('/admin/users').then(asItems),
  setUserScope: (id: string, gesellschaftIds: string[], expectedRowVersion: number) =>
    request<UserView>(`/admin/users/${enc(id)}/scope`, { method: 'PUT', body: { gesellschaftIds, expectedRowVersion } }),
};

export type ModelUpsert = {
  name: string;
  provider: ModelConfigView['provider'];
  modelId: string;
  endpoint: string | null;
  roles: ModelConfigView['roles'];
  contextLimitTokens: number;
  inputBudgetTokens: number;
  outputLimitTokens: number;
  timeoutSeconds: number;
  active: boolean;
  expectedRowVersion?: number;
};

export type ReviewProfileUpsert = {
  name: string;
  correctnessModelConfigId: string;
  errorRiskModelConfigId: string;
  active: boolean;
};

/** Akzeptiert sowohl Page<T> als auch ein nacktes Array (Backend-Form noch offen). */
function asItems<T>(data: Page<T> | T[]): T[] {
  return Array.isArray(data) ? data : (data?.items ?? []);
}

/** Alle Seiten laden (keine verdeckte Begrenzung auf die erste Seite, 02 „Auswahl“). */
export async function fetchAllPages<T>(load: (cursor?: string) => Promise<Page<T>>, hardCap = 20000): Promise<T[]> {
  const out: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await load(cursor);
    out.push(...page.items);
    cursor = page.nextCursor ?? undefined;
  } while (cursor && out.length < hardCap);
  return out;
}
